import { FormGroup } from '@angular/forms';
import { getControl, getFormArray } from '@shared/helpers/form.helper';
import { debounceTime, merge, startWith, take } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { cloneDeep } from 'lodash-es';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { revisione53DatiFormSelectors } from '../../../store/selectors/dati-form.selectors';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { getBaseSocialeControl, getBaseSocialeGroup } from '../validators/helpers/base-sociale-form.helper';

export const setBaseSocialeBusinessListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    manageMancataRedazione(form, store);
    my_ckAttivFacchinaggio(form, store);
    manageContrUsatiDescNome(form, store);
    manageContrUsatiSociLavoratoriDescNome(form, store);
    manageEnableDisableFromSociSpecData(form, store);
};

const manageMancataRedazione = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_CkNormeSocioLavoratControl = getBaseSocialeControl(form, 'my_CkNormeSocioLavorat');
    const my_CkRedazRegolamControl = getBaseSocialeControl(form, 'my_CkRedazRegolam');

    merge(
        my_CkNormeSocioLavoratControl.valueChanges.pipe(startWith(my_CkNormeSocioLavoratControl.value)),
        my_CkRedazRegolamControl.valueChanges.pipe(startWith(my_CkRedazRegolamControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const visibValue = my_CkRedazRegolamControl.value === '0' ? '1' : '0';
            const my_ElWarningFormArray = getFormArray(form, 'my_Warning.my_ElWarning');
            if (my_ElWarningFormArray?.controls?.[3]) {
                getControl(my_ElWarningFormArray.controls[3] as FormGroup, 'my_VisibWarning')?.patchValue(visibValue, { emitEvent: false });
            }
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                if (myVerbaleClone.my_Warning.my_ElWarning?.[3]) {
                    myVerbaleClone.my_Warning.my_ElWarning[3].my_VisibWarning = visibValue;
                }
                return myVerbaleClone;
            });
        });
};

const my_ckAttivFacchinaggio = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_ckAttivFacchinControl = getBaseSocialeControl(form, 'my_gruppo13.my_ckAttivFacchin');
    const my_ckAttivFacchinaggioEsclusControl = getBaseSocialeControl(form, 'my_ckAttivFacchinaggioEsclus');
    const my_ckAttivFacchinaggioControl = getBaseSocialeControl(form, 'my_gruppo13.my_ckAttivFacchinaggio');
    merge(
        my_ckAttivFacchinControl.valueChanges.pipe(startWith(my_ckAttivFacchinControl.value)),
        my_ckAttivFacchinaggioEsclusControl.valueChanges.pipe(startWith(my_ckAttivFacchinaggioEsclusControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const my_ckAttivFacchin: string = my_ckAttivFacchinControl.value;
            const my_ckAttivFacchinaggio: string = my_ckAttivFacchinaggioEsclusControl.value;
            if (my_ckAttivFacchinaggio === '1') {
                my_ckAttivFacchinaggioControl.setValue('1', { emitEvent: false });
            }

            if (my_ckAttivFacchin === '0' || my_ckAttivFacchinaggio === '0') {
                my_ckAttivFacchinaggioControl.setValue('0', { emitEvent: false });
            }

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_gruppo13.my_ckAttivFacchinaggio = my_ckAttivFacchinaggioControl.value;
                return myVerbaleClone;
            });
        });
};

const manageContrUsatiDescNome = (form: FormGroup, store: Store<Revisione53State>): void => {
    const effGroup = getBaseSocialeGroup(form);
    const formArray = getFormArray(effGroup, 'my_ContrattiUsati.my_ElContrattiUsati');

    formArray.valueChanges.pipe(withChangeDebounced(formArray)).subscribe(() => {
        store
            .select(Revisione53DatiBusinessSelectors.getContratti)
            .pipe(take(1))
            .subscribe((contratti) => {
                (formArray.controls as FormGroup[]).forEach((rowGroup: FormGroup) => {
                    const codControl = getControl(rowGroup, 'my_ContrUsatiDesc');
                    const nomeControl = getControl(rowGroup, 'my_ContrattiUsatiDescNome');
                    if (isNilOrEmpty(codControl?.value)) return;
                    const contratto = contratti?.find((c) => c.id_contratto === codControl.value);
                    nomeControl?.setValue(contratto?.descrizione ?? '', { emitEvent: false });
                });
                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                    const items = myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_ContrattiUsati?.my_ElContrattiUsati ?? [];
                    items.forEach((item) => {
                        const contratto = contratti?.find((c) => c.id_contratto === item.my_ContrUsatiDesc);
                        item.my_ContrattiUsatiDescNome = isNilOrEmpty(item.my_ContrUsatiDesc) ? item.my_ContrattiUsatiDescNome : contratto?.descrizione ?? '';
                    });
                    return myVerbaleClone;
                });
            });
    });
};

const manageEnableDisableFromSociSpecData = (form: FormGroup, store: Store<Revisione53State>): void => {
    const ckSociSpecDiritti = getBaseSocialeControl(form, 'my_CkSociSpecDiritti');
    const ckSociSpecLimite = getBaseSocialeControl(form, 'my_CkSociSpecLimite');

    store.select(revisione53DatiFormSelectors.getDisablingConditionFromSociSpecData).subscribe((condition) => {
        if (condition) {
            ckSociSpecDiritti.disable({ emitEvent: false });
            ckSociSpecLimite.disable({ emitEvent: false });
        } else {
            ckSociSpecDiritti.enable({ emitEvent: false });
            ckSociSpecLimite.enable({ emitEvent: false });
        }
    });
};

const manageContrUsatiSociLavoratoriDescNome = (form: FormGroup, store: Store<Revisione53State>): void => {
    const effGroup = getBaseSocialeGroup(form);
    const formArray = getFormArray(effGroup, 'my_ContrattiUsatiSociLavoratori.my_ElContrattiUsatiSociLavoratori');

    formArray.valueChanges.pipe(withChangeDebounced(formArray)).subscribe(() => {
        store
            .select(Revisione53DatiBusinessSelectors.getContratti)
            .pipe(take(1))
            .subscribe((contratti) => {
                (formArray.controls as FormGroup[]).forEach((rowGroup: FormGroup) => {
                    const codControl = getControl(rowGroup, 'my_ContrattiUsatiSociLavoratoriDesc');
                    const nomeControl = getControl(rowGroup, 'my_ContrattiUsatiSociLavoratoriDescNome');
                    if (isNilOrEmpty(codControl?.value)) return;
                    const contratto = contratti?.find((c) => c.id_contratto === codControl.value);
                    nomeControl?.setValue(contratto?.descrizione ?? '', { emitEvent: false });
                });
                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                    const items = myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_ContrattiUsatiSociLavoratori?.my_ElContrattiUsatiSociLavoratori ?? [];
                    items.forEach((item) => {
                        const contratto = contratti?.find((c) => c.id_contratto === item.my_ContrattiUsatiSociLavoratoriDesc);
                        item.my_ContrattiUsatiSociLavoratoriDescNome = isNilOrEmpty(item.my_ContrattiUsatiSociLavoratoriDesc)
                            ? item.my_ContrattiUsatiSociLavoratoriDescNome
                            : contratto?.descrizione ?? '';
                    });
                    return myVerbaleClone;
                });
            });
    });
};
